"use client";

import type { ApexOptions } from "apexcharts";
import dynamic from "next/dynamic";
import { useTheme } from "@/providers";
import { Text, View } from "@/components";

// Lazy load the chart to avoid SSR issues
const ReactApexChart = dynamic(() => import("react-apexcharts"), {
  ssr: false
});

const DEFAULT_COLORS = [
  "#5D69F4",
  "#00998B",
  "#FFC861",
  "#FF6161",
  "#8169D3",
  "#38BDF8",
  "#F472B6",
  "#A3E635",
  "#94A3B8"
];

export interface PieProps {
  /** Card title shown above the chart */
  title: string;
  /** The labels corresponding to each slice */
  labels: string[];
  /** The data series to display in the chart (one value per slice) */
  series: number[];
  /** The colors for each slice */
  colors?: string[];
  /** Render as donut instead of solid pie */
  donut?: boolean;
  /** Chart height in pixels */
  height?: number;
  /** Optional ApexCharts configuration to merge with defaults */
  options?: ApexOptions;
}

/**
 * @component
 * @name Pie
 * @description A customizable pie/donut chart card. Follows the same
 * theme-aware pattern as the Polar organism (lazy client-only ApexCharts,
 * light/dark via el ThemeProvider del template) with a plain title header.
 *
 * @example
 * ```tsx
 * <Pie
 *   title="Certificados por municipio"
 *   labels={["La Plata", "Quilmes", "Sin especificar"]}
 *   series={[12, 7, 3]}
 *   donut
 * />
 * ```
 */
const Pie = ({
  title,
  labels,
  series,
  colors = DEFAULT_COLORS,
  donut = false,
  height = 350,
  options
}: PieProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const chartType = donut ? "donut" : "pie";

  const defaultOptions: ApexOptions = {
    chart: {
      type: chartType,
      background: "transparent",
      animations: { enabled: true },
      fontFamily: "inherit",
      toolbar: { show: false }
    },
    labels: labels,
    colors: colors,
    stroke: {
      width: 1,
      colors: [isDark ? "#375281" : "#e2e8f0"]
    },
    legend: {
      show: true,
      position: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      fontWeight: 400,
      labels: { colors: isDark ? "#c8d2e4" : "#475569" },
      markers: { size: 6, offsetX: -4 },
      itemMargin: { vertical: 6, horizontal: 10 }
    },
    theme: {
      mode: isDark ? "dark" : "light"
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", fontWeight: 600 },
      dropShadow: { enabled: false }
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      fillSeriesColor: false
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: { position: "bottom" }
        }
      }
    ]
  };

  return (
    <View className="w-full p-6 rounded-2xl bg-card border border-border shadow-sm">
      <View className="pb-4 mb-2 border-b border-dashed border-border">
        <Text className="text-base font-semibold text-foreground">{title}</Text>
      </View>
      <View className="flex items-center justify-center" aria-label={title}>
        <View className="w-full h-full max-w-lg">
          <ReactApexChart
            options={
              options ? { ...defaultOptions, ...options } : defaultOptions
            }
            series={series}
            type={chartType}
            height={height}
            width="100%"
          />
        </View>
      </View>
    </View>
  );
};

export default Pie;
